import { findRow, readTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { activeDiscCount } from './discrepancy.handler'
import { PAL_CAP, PAL_REC, stockFg, stockRm } from './inventoryStock'
import { INV_SCOPE_ROLES } from './invScope'
import { autoApproveSweep } from './mergeConsolidation.handler'
import { lossPanel } from './opname.handler'
import { binsOfRm } from './rackScope'
import { almAutoApprove, slaDays, slaHours } from './slaSetting'
import { isTobaccoMaterialId } from './tobaccoGuard'

/**
 * Agregasi baca-saja INV-03 Dashboard Operasional: kartu aksi + grafik per cakupan peran (FG hanya melihat dunia FG,
 * RM hanya dunia RM). Ambang SLA dibaca dari Master SLA (MST-08): umur karantina dan batas terima batch FG;
 * dokumen approval tertahan 24 jam masih tetap.
 */

/** Ambang Master SLA (MST-08) dibaca saat dipakai; dokumen approval tertahan 24 jam belum punya baris di Master SLA. */
export const slaThresholds = () =>
  ({
    quarantine_days: slaDays('QUARANTINE_MAX_AGE'),
    fg_receive_hours: slaHours('FG_RECEIVE_LIMIT'),
    approval_hours: 24
  }) as const
/** Foot kartu "Selisih FG dalam investigasi" dari Master SLA `DSC_APPROVAL_FG` (bawaan 1 hari = teks prototype). */
export const discDeadlineFg = () => {
  const days = slaDays('DSC_APPROVAL_FG')
  return days <= 1 ? 'Tenggat penyelesaian hari itu juga' : `Tenggat penyelesaian ${days} hari`
}

/** Role dunia FG = `INV_SCOPE_ROLES.fg` (satu daftar dengan INV-01). */
const FG_ROLES: readonly string[] = INV_SCOPE_ROLES.fg

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')
const num = (value: unknown) => Number(value ?? 0)
const round1 = (value: number) => Math.round(value * 10) / 10
const statusCode = (row: LocalRow) => String(findRow('wml_status', idOf(row.status_id))?.code ?? '')
const hoursSince = (iso: unknown) => (Date.now() - new Date(String(iso ?? '')).getTime()) / 3600_000
const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}
/** Hari (Jakarta) dari stempel ISO, bentuk `YYYY-MM-DD`. */
const dayOf = (iso: unknown) => {
  const date = new Date(String(iso ?? ''))
  return Number.isNaN(date.getTime()) ? '' : new Date(date.getTime() + 7 * 3600_000).toISOString().slice(0, 10)
}
const lastDays = (count: number) => {
  const today = new Date(Date.now() + 7 * 3600_000)
  return Array.from({ length: count }, (_, index) => {
    const day = new Date(today.getTime() - (count - 1 - index) * 86400_000)
    return day.toISOString().slice(0, 10)
  })
}

export type Tone = 'danger' | 'warning' | 'info' | 'success' | 'neutral'
export type Card = {
  key: string
  n: number
  tone: Tone
  chip: string
  chip_tone: Tone
  label: string
  foot: string
  to: string | null
}

const uptime = (hours: number) => {
  const total = Math.max(0, Math.round(hours * 60))
  const day = Math.floor(total / 1440)
  const hour = Math.floor((total % 1440) / 60)
  const minute = total % 60
  return day ? `${day}h ${hour}j` : hour ? `${hour}j ${minute}m` : `${minute}m`
}

const openTasks = (isFg: boolean) => {
  const load = new Map<string, number>()
  const add = (operator: unknown) => {
    const id = idOf(operator)
    if (id) load.set(id, (load.get(id) ?? 0) + 1)
  }
  const open = (row: LocalRow) => !['COMPLETED', 'CANCELLED'].includes(String(row.task_status))
  if (isFg) {
    readTable('fg_putaway_task')
      .filter(open)
      .forEach(row => add(row.operator_id))
    readTable('fg_picking_task')
      .filter(open)
      .forEach(row => add(row.operator_id))
    readTable('fg_rts_task')
      .filter(open)
      .forEach(row => add(row.operator_id))
    readTable('consolidation_task')
      .filter(row => !['KSL_DONE', 'KSL_STOPPED'].includes(statusCode(row)))
      .forEach(row => add(row.operator_id))
  } else {
    /* v7.50 U-14: beban QC hanya Assigned / In Progress — hasil yang Menunggu Persetujuan (QC_PENDING_APPROVAL)
     * sudah lepas dari tangan operator (prototype command-center: bukan 'Menunggu Persetujuan' / 'Disetujui'). */
    readTable('rm_qc_assignment')
      .filter(row => ['QC_ASSIGNED', 'QC_IN_PROGRESS'].includes(statusCode(row)))
      .forEach(row => add(row.operator_id))
    readTable('rm_putaway_task')
      .filter(open)
      .forEach(row => add(row.operator_id))
    readTable('picking_task')
      .filter(open)
      .forEach(row => add(row.operator_id))
    readTable('rm_move_task')
      .filter(row => ['ASSIGNED', 'IN_PROGRESS'].includes(String(row.task_status)))
      .forEach(row => add(row.operator_id))
  }
  return [...load.entries()]
    .map(([id, tasks]) => ({ name: userName(id).split(' ')[0] || id, tasks }))
    .sort((a, b) => b.tasks - a.tasks)
}

export function flow14(isFg: boolean) {
  const days = lastDays(14)
  const tx = readTable('tx_log')
  const countTx = (day: string, actions: string[]) =>
    tx.filter(row => dayOf(row.ts) === day && actions.includes(String(row.action))).length
  const incoming = days.map(day => {
    if (isFg) {
      return (
        countTx(day, ['fg-putaway']) + readTable('fg_labeler_batch').filter(batch => batch.prod_date === day).length
      )
    }
    return readTable('rm_unit').filter(unit => dayOf(unit.stored_at) === day).length
  })
  const outgoing = days.map(day => {
    if (isFg) return countTx(day, ['plan-ship', 'fg-stage', 'fg-pick'])
    return readTable('stock_movement').filter(move => move.movement_type === 'RM_PICK' && dayOf(move.ts) === day).length
  })
  const peak = incoming.indexOf(Math.max(...incoming))
  return {
    days,
    incoming,
    outgoing,
    peak_day: Math.max(...incoming) > 0 ? days[peak] : null,
    peak_count: Math.max(...incoming),
    average: round1(incoming.reduce((sum, value) => sum + value, 0) / days.length)
  }
}

/**
 * Supply tembakau harian per mesin (prototype `inv-03.html` `tbkHtml`): hari terakhir yang punya pengambilan tembakau ke
 * mesin — keluar per mesin (hijau) lalu retur sisa per jenis pada hari itu (amber), dalam kg (Sak × kg per Sak).
 */
export function tobaccoSupply() {
  const kgPerSak = (unit: LocalRow | undefined, material: LocalRow | undefined) =>
    Math.max(1, num(unit?.small_per_big_snapshot) || num(material?.default_purchase_unit_qty) || 1)
  const picks = readTable('picking_task_item').flatMap(item => {
    const task = findRow('picking_task', idOf(item.task_id))
    const machine = findRow('mt_equipment', idOf(task?.machine_id))
    const unit = findRow('rm_unit', idOf(item.unit_id))
    const qty = num(item.qty_taken) - num(item.qty_returned)
    if (item.status !== 'PICKED' || qty <= 0 || !machine || !isTobaccoMaterialId(idOf(unit?.material_id))) return []
    const material = findRow('mt_material', idOf(unit?.material_id))
    return [{ day: dayOf(item.picked_at), machine: String(machine.code ?? ''), kg: qty * kgPerSak(unit, material) }]
  })
  const date = picks
    .map(pick => pick.day)
    .filter(Boolean)
    .sort()
    .at(-1)
  if (!date) return null
  const out = new Map<string, number>()
  picks.filter(pick => pick.day === date).forEach(pick => out.set(pick.machine, (out.get(pick.machine) ?? 0) + pick.kg))
  const returns = new Map<string, number>()
  const returnIds = new Set(
    readTable('rm_tobacco_return')
      .filter(row => dayOf(row.date_created) === date)
      .map(row => row.id)
  )
  readTable('rm_tobacco_return_line')
    .filter(line => returnIds.has(idOf(line.return_id)))
    .forEach(line => {
      const material = findRow('mt_material', idOf(line.material_id))
      const code = String(material?.code ?? '').replace('TSG-', '')
      returns.set(code, (returns.get(code) ?? 0) + num(line.qty_sak) * kgPerSak(undefined, material))
    })
  const short = (code: string) => code.replace('MOLLINS', 'MOL').replace('PROTOS', 'PRO')
  return {
    date,
    bars: [
      ...[...out.entries()].map(([machine, kg]) => ({
        label: short(machine),
        value: round1(kg),
        kind: 'out' as const
      })),
      ...[...returns.entries()].map(([code, kg]) => ({
        label: `Retur ${code}`,
        value: round1(kg),
        kind: 'return' as const
      }))
    ]
  }
}

function dashboardRm() {
  const rm = stockRm({ kelompok: 'all' })
  const quarantine = readTable('rm_quarantine_entry').filter(entry => entry.quarantine_status === 'IN_QUARANTINE')
  const ageDays = (entry: LocalRow) => hoursSince(entry.entered_at) / 24
  const overSla = quarantine.filter(entry => ageDays(entry) > slaThresholds().quarantine_days).length
  const avg = quarantine.length
    ? round1(quarantine.reduce((sum, entry) => sum + ageDays(entry), 0) / quarantine.length)
    : 0

  const critical = rm.materials
    .filter(row => row.status_stok === 'PESAN')
    .sort((a, b) => (a.days_of_cover ?? 0) - (b.days_of_cover ?? 0))
  const hasPr = new Set(
    readTable('purchase_request_line')
      .filter(line => {
        const pr = findRow('purchase_request', idOf(line.pr_id))
        return pr && !/REJECTED/.test(statusCode(pr))
      })
      .map(line => idOf(line.material_id))
  )
  const ngOpen = readTable('ng_ticket').filter(ticket => ticket.ticket_status === 'OPEN')
  const roRunning = readTable('return_order').filter(
    row => !['RTN_CLOSED', 'RTN_REJECTED', 'RTN_CANCELLED', 'RTN_DRAFT'].includes(statusCode(row))
  )
  const reqOpen = readTable('material_request').filter(
    row => !['REQ_COMPLETED', 'REQ_CANCELLED', 'REQ_DRAFT'].includes(statusCode(row))
  )
  const pendingDocs = [
    ...readTable('purchase_request')
      .filter(row => ['PRC_PENDING_SPV', 'PRC_PENDING_PURCH'].includes(statusCode(row)))
      .map(row => ({ jenis: 'PR', id: String(row.pr_no), at: row.date_updated ?? row.date_created })),
    ...readTable('transfer')
      .filter(row => ['TRF_PENDING_SPV', 'TRF_PENDING_KADIV'].includes(statusCode(row)))
      .map(row => ({ jenis: 'Transfer', id: String(row.itr_no), at: row.date_updated ?? row.date_created }))
  ].sort((a, b) => String(a.at).localeCompare(String(b.at)))
  const backlog = pendingDocs
    .filter(doc => hoursSince(doc.at) > slaThresholds().approval_hours)
    .map(doc => ({
      jenis: doc.jenis,
      id: doc.id,
      note: `menunggu persetujuan ${uptime(hoursSince(doc.at))}`,
      to: doc.jenis === 'PR' ? 'procurement/approval-pr' : null
    }))
  const bins = binsOfRm().filter(bin => bin.is_active !== false)
  const used = new Set(
    rm.materials
      .flatMap(row => row.units)
      .filter(unit => unit.group === 'stok' && unit.rack_bin_code)
      .map(unit => unit.rack_bin_code)
  )
  const unitsInStock = rm.counts.stok
  const percent = bins.length ? Math.round((used.size / bins.length) * 100) : 0

  const actions: Card[] = [
    {
      key: 'karantina',
      n: quarantine.length,
      tone: overSla ? 'danger' : quarantine.length ? 'info' : 'neutral',
      chip: overSla ? `${overSla} lewat SLA` : 'Antrean QC',
      chip_tone: overSla ? 'danger' : 'info',
      label: 'Batch di Karantina Kedatangan',
      foot: quarantine.length
        ? `Rata-rata ${String(avg).replace('.', ',')} hari · ambang ${slaThresholds().quarantine_days} hari`
        : 'Karantina kosong',
      to: 'inbound-rm/qc-scheduler'
    },
    {
      key: 'pesan',
      n: critical.length,
      tone: critical.length ? 'danger' : 'success',
      chip: critical.length ? 'Perlu PR' : 'Aman',
      chip_tone: critical.length ? 'danger' : 'success',
      label: 'Material berstatus PESAN',
      foot: critical.length
        ? `Tersingkat ${critical[0].days_of_cover ?? 0} hari — ${critical[0].code}`
        : 'Seluruh material di atas titik pesan',
      to: 'procurement/daftar-pr'
    },
    {
      key: 'ng',
      n: ngOpen.length,
      tone: ngOpen.length ? 'warning' : 'neutral',
      chip: ngOpen.length ? 'Belum ditindak' : 'Klir',
      chip_tone: ngOpen.length ? 'warning' : 'neutral',
      label: 'Tiket material NG terbuka',
      foot: roRunning.length ? `${roRunning.length} Return Order berjalan` : 'Tidak ada Return Order berjalan',
      to: 'return-order-rm/daftar-ro'
    },
    {
      key: 'spk',
      n: reqOpen.length,
      tone: reqOpen.length ? 'info' : 'neutral',
      chip: 'Produksi',
      chip_tone: 'info',
      label: 'Request SPK terbuka',
      foot: reqOpen.length ? 'Menunggu penugasan atau serah terima' : 'Seluruh request tuntas',
      to: 'request-production/request-material'
    },
    {
      key: 'approval',
      n: pendingDocs.length,
      tone: pendingDocs.length ? 'warning' : 'neutral',
      chip: 'Menunggu approval',
      chip_tone: 'warning',
      label: 'Dokumen menunggu persetujuan',
      foot: pendingDocs.length
        ? `Tertua tertahan ${uptime(hoursSince(pendingDocs[0].at))}`
        : 'Tidak ada antrean approval',
      to: 'procurement/approval-pr'
    },
    {
      key: 'selisih',
      n: activeDiscCount('RM'),
      tone: activeDiscCount('RM') ? 'warning' : 'neutral',
      chip: activeDiscCount('RM') ? 'Investigasi' : 'Klir',
      chip_tone: activeDiscCount('RM') ? 'warning' : 'neutral',
      label: 'Selisih RM dalam investigasi',
      foot: activeDiscCount('RM') ? 'Telusuri jejak barang di Detail Selisih & Write-off' : 'Rekonsiliasi opname klir',
      to: activeDiscCount('RM') ? 'stock-opname/selisih' : null
    },
    {
      key: 'backlog',
      n: backlog.length,
      tone: backlog.length ? 'danger' : 'neutral',
      chip: backlog.length ? 'Lewat tenggat' : 'Aman',
      chip_tone: backlog.length ? 'danger' : 'neutral',
      label: 'Dokumen backlog lewat SLA',
      foot: backlog.length ? `${backlog[0].jenis} ${backlog[0].id}` : 'Semua dokumen dalam tenggat',
      to: backlog[0]?.to ?? 'return-order-rm/daftar-ro'
    },
    {
      key: 'stok',
      n: unitsInStock,
      tone: 'neutral',
      chip: 'Stok',
      chip_tone: 'neutral',
      label: 'Unit RM In Stock',
      foot: `${used.size} dari ${bins.length} posisi rack terpakai`,
      to: 'inventory-reports/stok-inventory'
    }
  ]
  return {
    actions,
    critical: critical.slice(0, 6).map(row => ({
      code: row.code,
      name: row.name,
      days: row.days_of_cover ?? 0,
      saldo: row.saldo_small,
      unit: row.unit_small,
      has_pr: hasPr.has(row.material_id),
      // D97: Tembakau tidak dipesan lewat PR — "+ Buat PR" tidak ditawarkan.
      is_tobacco: isTobaccoMaterialId(row.material_id)
    })),
    critical_total: critical.length,
    capacity: {
      title: 'Kapasitas Rak RM',
      percent,
      used: used.size,
      total: bins.length,
      unit_label: 'posisi rack',
      meters: [{ label: 'Unit In Stock', value: unitsInStock, max: Math.max(unitsInStock, bins.length), unit: 'unit' }],
      note: 'Satu posisi hanya boleh berisi satu jenis material, tetapi boleh menampung beberapa batch dari material yang sama.',
      link: 'inventory-reports/stok-inventory',
      link_label: 'Stok RM'
    },
    quarantine: quarantine
      .sort((a, b) => ageDays(b) - ageDays(a))
      .slice(0, 6)
      .map(entry => {
        const line = findRow('rm_arrival_line', idOf(entry.arrival_line_id))
        return {
          id: String(entry.quarantine_no).replace('QRT-', ''),
          age_days: Math.round(ageDays(entry)),
          material: String(findRow('mt_material', idOf(line?.material_id))?.code ?? '—'),
          over: ageDays(entry) > slaThresholds().quarantine_days
        }
      }),
    quarantine_target: slaThresholds().quarantine_days,
    quarantine_over: overSla,
    loss: lossPanel('RM'),
    backlog,
    flow: flow14(false),
    load: openTasks(false),
    tobacco_supply: tobaccoSupply()
  }
}

/** Jam:menit WIB dari stempel ISO. */
const hhmm = (iso: unknown) =>
  new Date(new Date(String(iso ?? '')).getTime() + 7 * 3600_000).toISOString().slice(11, 16)

/**
 * Kartu Permintaan ALM per peran (v7.50 A-01): Admin memutuskan; Supervisor hanya memantau ("menunggu Admin — disetujui
 * otomatis"); Kadiv melihat yang berjalan. Pembacaan menjalankan auto-approve lebih dulu supaya angka mutakhir.
 */
export function almCards(roleCodes: string[]): Card[] {
  const isAdmin = roleCodes.includes('admin_wh_fg')
  const isSupervisor = roleCodes.includes('supervisor_wh_fg')
  const isKadiv = roleCodes.includes('kadiv_wh_fg')
  if (!isAdmin && !isSupervisor && !isKadiv) return []
  autoApproveSweep()
  const requests = readTable('merge_request').filter(req => req.kind === 'ALM')
  const pending = requests
    .filter(req => statusCode(req) === 'GAB_PENDING')
    .sort((a, b) => String(a.date_created).localeCompare(String(b.date_created)))
  const running = requests.filter(req => ['GAB_READY', 'GAB_PICKING'].includes(statusCode(req)))
  const rule = almAutoApprove()
  const first = pending[0]
  const cards: Card[] = []
  if (isAdmin)
    cards.push({
      key: 'alm-pend',
      n: pending.length,
      tone: pending.length ? 'danger' : 'neutral',
      chip: pending.length ? 'ALM diblokir' : 'Kosong',
      chip_tone: pending.length ? 'danger' : 'neutral',
      label: 'Permintaan ALM menunggu persetujuan saya',
      foot: first
        ? `ALM ${num(first.alm_no)} diblokir sejak ${hhmm(first.date_created)} · ${Math.floor(hoursSince(first.date_created) * 60)} mnt`
        : 'Tidak ada ALM yang diblokir',
      to: 'merge-consolidation-fg/barang-lepas-gabung?tab=alm'
    })
  else if (isSupervisor)
    cards.push({
      key: 'alm-pantau',
      n: pending.length,
      tone: pending.length ? 'warning' : 'neutral',
      chip: pending.length ? 'Menunggu Admin' : 'Kosong',
      chip_tone: pending.length ? 'warning' : 'neutral',
      label: 'Permintaan ALM menunggu Admin — disetujui otomatis',
      foot: !pending.length
        ? 'Tidak ada permintaan menunggu'
        : rule.enabled
          ? `Disetujui otomatis bila belum diputuskan dalam ${rule.value} ${rule.unit === 'HOUR' ? 'jam' : 'mnt'}`
          : 'Persetujuan otomatis dimatikan di Master SLA — menunggu Admin',
      to: 'merge-consolidation-fg/barang-lepas-gabung?tab=alm'
    })
  cards.push({
    key: 'alm-run',
    n: running.length,
    tone: running.length ? 'info' : 'neutral',
    chip: running.length ? 'Berjalan' : 'Kosong',
    chip_tone: running.length ? 'info' : 'neutral',
    label: 'Permintaan ALM berjalan',
    foot: `Menunggu diambil ${running.filter(req => statusCode(req) === 'GAB_READY').length} · Diambil ${running.filter(req => statusCode(req) === 'GAB_PICKING').length}`,
    to: 'merge-consolidation-fg/barang-lepas-gabung?tab=alm'
  })
  return cards
}

function dashboardFg(roleCodes: string[]) {
  const fg = stockFg({})
  const batches = readTable('fg_labeler_batch')
  const held = batches.filter(batch => statusCode(batch) === 'FG_HELD')
  const ready = batches.filter(batch => statusCode(batch) === 'FG_READY_HANDOVER')
  const late = ready.filter(batch => hoursSince(batch.date_created) > slaThresholds().fg_receive_hours)
  const putaway = readTable('fg_putaway_task').filter(
    task => !['COMPLETED', 'CANCELLED'].includes(String(task.task_status))
  )
  const plans = readTable('delivery_plan')
  const forecast = plans.filter(plan => statusCode(plan) === 'OFG_FORECAST' && !plan.is_void)
  const staged = plans.filter(plan => statusCode(plan) === 'OFG_STAGED' && !plan.is_void)
  const consolidation = readTable('consolidation_task').filter(task =>
    ['KSL_ASSIGNED', 'KSL_LOADED'].includes(statusCode(task))
  )
  /* Pallet nonaktif (Master Lokasi Gudang) tidak dihitung sebagai kapasitas, kecuali masih berisi. */
  const palletsAll = fg.zones
    .flatMap(zone => zone.pallets)
    .filter(pallet => pallet.is_active || pallet.units.length > 0)
  const slotUsed = palletsAll.filter(pallet => pallet.units.length > 0).length
  const keq = Math.round(palletsAll.reduce((sum, pallet) => sum + pallet.keq, 0) * 10) / 10
  const labels = readTable('fg_label').filter(label => label.label_status === 'IN_STOCK')
  const onPallet = labels.filter(label => label.pallet_id).length
  const inContainer = labels.filter(label => !label.pallet_id && label.container_id).length
  const loose = labels.length - onPallet - inContainer
  const lateAge = late.length ? Math.max(...late.map(batch => hoursSince(batch.date_created))) : 0

  const planWeekEnd = (plan: LocalRow) => {
    const match = /^(\d{4})-(\d{2})-W(\d)$/.exec(String(plan.period ?? ''))
    if (!match) return null
    return new Date(Number(match[1]), Number(match[2]) - 1, Math.min(28, Number(match[3]) * 7)).getTime()
  }
  const backlog = [
    ...forecast,
    ...plans.filter(plan => ['OFG_RELEASED', 'OFG_STAGING', 'OFG_STAGED'].includes(statusCode(plan)))
  ]
    .filter(plan => !plan.is_void && (planWeekEnd(plan) ?? Number.POSITIVE_INFINITY) < Date.now())
    .map(plan => ({
      jenis: 'Order Pengiriman',
      id: String(plan.plan_no),
      note: `periode ${plan.period} sudah lewat, belum Shipped`,
      to: 'outbound-fg/order-pengiriman'
    }))

  const actions: Card[] = [
    ...almCards(roleCodes),
    {
      key: 'held',
      n: held.length,
      tone: held.length ? 'danger' : 'neutral',
      chip: held.length ? 'Perlu tindakan' : 'Aman',
      chip_tone: held.length ? 'danger' : 'neutral',
      label: 'Batch ditahan menunggu keputusan',
      foot: held.length
        ? held.map(batch => String(batch.batch_no).split('-').slice(0, 2).join('-')).join(' · ')
        : 'Tidak ada batch tertahan',
      to: 'inbound-fg/penerimaan-fg'
    },
    {
      key: 'terima',
      n: ready.length,
      tone: late.length ? 'warning' : ready.length ? 'info' : 'neutral',
      chip: late.length ? `${late.length} lewat tenggat` : 'Antrean',
      chip_tone: late.length ? 'warning' : 'info',
      label: 'Batch menunggu Terima & Cek',
      foot: late.length
        ? `Terlama ${uptime(lateAge)} · ambang ${slaThresholds().fg_receive_hours} jam`
        : `Semua masih dalam ambang ${slaThresholds().fg_receive_hours} jam`,
      to: 'inbound-fg/penerimaan-fg'
    },
    {
      key: 'konsolidasi',
      n: consolidation.length,
      tone: consolidation.length ? 'info' : 'neutral',
      chip: 'Saran sistem',
      chip_tone: 'info',
      label: 'Tugas konsolidasi pallet berjalan',
      foot: consolidation.length ? 'Memadatkan pallet longgar' : 'Tidak ada pallet yang perlu dipadatkan',
      to: 'merge-consolidation-fg/konsolidasi-pallet'
    },
    {
      key: 'putaway',
      n: putaway.length,
      tone: putaway.length ? 'info' : 'neutral',
      chip: putaway.length ? 'Sedang dikerjakan' : 'Kosong',
      chip_tone: putaway.length ? 'info' : 'neutral',
      label: 'Putaway FG berjalan',
      foot: putaway.length ? `${putaway.length} tugas belum selesai` : 'Tidak ada unit berjalan',
      to: 'inbound-fg/putaway-fg-monitor'
    },
    {
      key: 'forecast',
      n: forecast.length,
      tone: forecast.length ? 'info' : 'neutral',
      chip: 'Forecast',
      chip_tone: 'info',
      label: 'Rencana kirim menunggu dirilis',
      foot: forecast.length ? `${forecast.length} rencana kirim` : 'Tidak ada rencana menunggu',
      to: 'outbound-fg/order-pengiriman'
    },
    {
      key: 'staged',
      n: staged.length,
      tone: staged.length ? 'success' : 'neutral',
      chip: staged.length ? 'Siap kirim' : 'Bersih',
      chip_tone: staged.length ? 'success' : 'neutral',
      label: 'Order siap ditandai Shipped',
      foot: `Area staging ${staged.length ? 'menunggu keberangkatan' : 'tuntas & bersih'}`,
      to: 'outbound-fg/order-pengiriman'
    },
    {
      key: 'selisih',
      n: activeDiscCount('FG'),
      tone: activeDiscCount('FG') ? 'warning' : 'neutral',
      chip: activeDiscCount('FG') ? 'Investigasi' : 'Klir',
      chip_tone: activeDiscCount('FG') ? 'warning' : 'neutral',
      label: 'Selisih FG dalam investigasi',
      foot: activeDiscCount('FG') ? discDeadlineFg() : 'Rekonsiliasi opname klir',
      to: activeDiscCount('FG') ? 'stock-opname/selisih' : null
    },
    {
      key: 'backlog',
      n: backlog.length,
      tone: backlog.length ? 'danger' : 'neutral',
      chip: backlog.length ? 'Lewat tenggat' : 'Aman',
      chip_tone: backlog.length ? 'danger' : 'neutral',
      label: 'Dokumen backlog lewat SLA',
      foot: backlog.length ? `${backlog[0].jenis} ${backlog[0].id}` : 'Semua dokumen dalam tenggat',
      to: backlog[0]?.to ?? 'outbound-fg/order-pengiriman'
    }
  ]
  return {
    actions,
    critical: [],
    critical_total: 0,
    capacity: {
      title: 'Kapasitas Gudang FG',
      percent: palletsAll.length ? Math.round((slotUsed / palletsAll.length) * 100) : 0,
      used: slotUsed,
      total: palletsAll.length,
      unit_label: 'slot pallet',
      meters: [
        {
          label: 'Isi pallet (karton ekuivalen)',
          value: keq,
          max: palletsAll.reduce((sum, pallet) => sum + pallet.capacity_keq, 0),
          unit: 'karton'
        },
        {
          label: `Batas anjuran ${PAL_REC}/pallet`,
          value: palletsAll.length * PAL_REC,
          max: palletsAll.reduce((sum, pallet) => sum + pallet.capacity_keq, 0),
          unit: 'karton'
        }
      ],
      note: `Dua ukuran sekaligus: slot pallet menjawab berapa pallet masih bebas, karton menjawab berapa barang masih muat. Kapasitas keras ${PAL_CAP} karton per pallet.`,
      link: 'inventory-reports/stok-inventory',
      link_label: 'Denah gudang'
    },
    spread: [
      { label: 'Di pallet', value: onPallet },
      { label: 'Di container', value: inContainer },
      { label: 'Lepas / buffer', value: loose }
    ],
    quarantine: [],
    quarantine_target: 0,
    quarantine_over: 0,
    loss: lossPanel('FG'),
    backlog,
    flow: flow14(true),
    load: openTasks(true)
  }
}

export function dashboard(roleCodes: string[]) {
  const isFg = roleCodes.some(code => FG_ROLES.includes(code))
  const body = isFg ? dashboardFg(roleCodes) : dashboardRm()
  const last = readTable('tx_log')
    .filter(row => {
      const wh = String(findRow('mt_warehouses', idOf(row.warehouse_id))?.code ?? '')
      return !wh || wh === (isFg ? 'WH-FG' : 'WH-RM')
    })
    .sort((a, b) => String(b.ts).localeCompare(String(a.ts)))[0]
  return {
    scope: isFg ? 'FG' : 'RM',
    sla: slaThresholds(),
    ...body,
    last_activity: last ? { detail: String(last.detail ?? last.action), ts: String(last.ts) } : null
  }
}
